import React, { lazy } from 'react';

import { Route } from 'react-router-dom';

import Protected from 'containers/Protected';
import { EL_VIRTUAL_TOURS, VIRTUAL_TOURS } from 'routes/constants';

const NCAAEligibility = lazy(
  () =>
    import(
      /* webpackChunkName: 'ncaa-eligibility' */ 'modules/NCAAEligibility'
    ),
);

const ElementaryCareers = lazy(
  () =>
    import(
      /* webpackChunkName: 'elementary-careers' */ 'modules/ElementaryCareers'
    ),
);

const InitiativePage = lazy(
  () =>
    import(/* webpackChunkName: 'initiative-page' */ 'modules/InitiativePage'),
);

const StudentDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-dashboard' */ 'modules/StudentDashboard'
    ),
);

const CollegeCosts = lazy(
  () =>
    import(
      /* webpackChunkName: 'college-costs' */ 'modules/CollegeCosts/routes'
    ),
);

const StudentPlanListing = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-plan-listing' */ 'modules/StudentPlanListing'
    ),
);
const StudentProfile = lazy(
  () =>
    import(/* webpackChunkName: 'student-profile' */ 'modules/StudentProfile'),
);

const Surveys = lazy(
  () => import(/* webpackChunkName: 'surveys' */ 'modules/WouldYouRather'),
);
const TestScores = lazy(
  () => import(/* webpackChunkName: 'test-scores' */ 'modules/TestScores'),
);
const VirtualTours = lazy(
  () => import(/* webpackChunkName: 'virtual-tours' */ 'modules/VirtualTours'),
);
const StudentGoals = lazy(
  () => import(/* webpackChunkName: 'student-goals' */ 'modules/StudentGoals'),
);

const VisitNotification = lazy(
  () =>
    import(
      /* webpackChunkName: 'visit-notification' */ 'modules/VisitNotification'
    ),
);

const PersonalizedPlans = lazy(
  () =>
    import(
      /* webpackChunkName: 'personalized-plans' */ 'modules/PersonalizedPlans'
    ),
);
const EmployerShowcase = lazy(
  () =>
    import(
      /* webpackChunkName: 'employer-showcase' */ 'modules/EmployerShowcase'
    ),
);
const StudentPathways = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-pathways' */ 'modules/StudentPathways'
    ),
);
const CredentialWallet = lazy(
  () =>
    import(
      /* webpackChunkName: 'credential-wallet' */ 'modules/CredentialWallet/routes'
    ),
);
const ShowcaseProfile = lazy(
  () =>
    import(
      /* webpackChunkName: 'showcase-profile' */ 'modules/EmployerShowcase/components/ShowcaseProfile'
    ),
);
const SchoolProfile = lazy(
  () =>
    import(/* webpackChunkName: 'school-profile' */ 'modules/SchoolProfile'),
);

const LifeSimulator = lazy(
  () =>
    import(/* webpackChunkName: 'life-simulator' */ 'modules/LifeSimulator'),
);
const LearningTasks = lazy(
  () =>
    import(/* webpackChunkName: 'learning-tasks' */ 'modules/LearningTasks'),
);

const SchoolSearch = lazy(
  () => import(/* webpackChunkName: 'school-search' */ 'modules/SchoolSearch'),
);

const SkillsAssessment = lazy(
  () =>
    import(
      /* webpackChunkName: 'skills-assessment' */ 'modules/SkillsAssessment'
    ),
);

const KeyReadinessIndicators = lazy(
  () =>
    import(
      /* webpackChunkName: 'key-readiness-indicators' */ 'modules/KeyReadinessIndicators/routes'
    ),
);
const CollegeApplicationManager = lazy(
  () =>
    import(
      /* webpackChunkName: 'application-manager' */ 'modules/CollegeApplicationManager/routes'
    ),
);

const FinalList = lazy(
  () => import(/* webpackChunkName: 'final-list' */ 'modules/FinalList'),
);

const RecordCenter = lazy(
  () =>
    import(
      /* webpackChunkName: 'transcripts' */ 'modules/RecordCenter/Student'
    ),
);

const AccountSettings = lazy(
  () =>
    import(
      /* webpackChunkName: 'account-settings' */ 'modules/AccountSettings'
    ),
);

const Assessments = lazy(
  () => import(/* webpackChunkName: 'assessments' */ 'modules/Assessments'),
);

const CareerCenterPages = lazy(
  () =>
    import(
      /* webpackChunkName: 'career-center' */ 'modules/CareerCenter/routes'
    ),
);

const CoursePlanner = lazy(
  () =>
    import(/* webpackChunkName: 'course-planner' */ 'modules/CoursePlanner'),
);

const DigitalBadges = lazy(
  () =>
    import(/* webpackChunkName: 'digital-badges' */ 'modules/DigitalBadges'),
);

const DocumentCenter = lazy(
  () =>
    import(/* webpackChunkName: 'document-center' */ 'modules/DocumentCenter'),
);

const MessageCenter = lazy(
  () =>
    import(/* webpackChunkName: 'message-center' */ 'modules/MessageCenter'),
);
const Notifications = lazy(
  () =>
    import(
      /* webpackChunkName: 'notifications' */ 'modules/Notifications/pages/Notifications'
    ),
);
const OnboardingAssessment = lazy(
  () =>
    import(
      /* webpackChunkName: 'onboarding-assessment' */ 'modules/Assessments/OnboardingAssessment'
    ),
);
const Opportunities = lazy(
  () => import(/* webpackChunkName: 'opportunities' */ 'modules/Opportunities'),
);

const ScholarshipMatching = lazy(
  () =>
    import(
      /* webpackChunkName: 'scholarship-matching' */ 'modules/ScholarshipMatching/pages/ScholarshipMatchPage'
    ),
);

const StudentExperienceTrackingDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-experience-tracking-detail-page' */ 'modules/ExperienceTracking/pages/StudentExperienceTrackingDetailPage'
    ),
);

const StudentExperienceTrackingLandingPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-experience-tracking-landing-page' */ 'modules/ExperienceTracking/pages/StudentExperienceTrackingLandingPage'
    ),
);

const PathV2WelcomeView = lazy(
  () =>
    import(
      /* webpackChunkName: 'path-result-welcome-view' */ 'modules/Assessments/components/PathResult/v2/WelcomeView/WelcomeView'
    ),
);

const SeniorSurvey = lazy(
  () => import(/* webpackChunkName: 'senior-survey' */ 'modules/SeniorSurvey'),
);

const IPPEStudentRoutes = lazy(
  () =>
    import(
      /* webpackChunkName: 'ippe-student-routes' */ 'modules/IndustryPartnerProgramExploration/Student/routes'
    ),
);

const EnrollmentDecision = lazy(
  () =>
    import(
      /* webpackChunkName: 'enrollment-decision' */ 'modules/CollegeApplicationManager/pages/EnrollmentDecision'
    ),
);

const K12UsersEventRedirect = lazy(
  () =>
    import(
      /* webpackChunkName: 'event-scheduler' */ 'modules/EventSchedulerV2/components/K12UsersEventRedirect'
    ),
);

const EventSchedulerSurveys = lazy(
  () =>
    import(
      /* webpackChunkName: 'event-scheduler-surveys' */ 'modules/EventSchedulerV2/components/EventSchedulerSurveys'
    ),
);

const PublicEventRedirect = lazy(
  () =>
    import(
      /* webpackChunkName: 'public-event-redirect' */ 'modules/EventSchedulerV2/Public/pages/PublicEventRedirect'
    ),
);

const MobileFileUpload = lazy(
  () =>
    import(
      /* webpackChunkName: 'mobile-file-upload' */ 'modules/MobileFileUpload/MobileFileUpload'
    ),
);

const BragSheetSurvey = lazy(
  () =>
    import(
      /* webpackChunkName: 'brag-sheet-survey' */ 'modules/BragSheetSurvey'
    ),
);

const Sitemap = lazy(
  () =>
    import(
      /* webpackChunkName: 'sitemap' */ 'modules/AccountSettings/components/Sitemap'
    ),
);

const StudentRoutes = () => (
  <>
    <Route path="/file-upload" element={<MobileFileUpload />} />
    <Route
      path="/public-event-redirect/:eventId/:districtId"
      element={<PublicEventRedirect />}
    />
    <Route
      path="/el/student/*"
      element={
        <Protected userType="student">
          <ElementaryCareers />
        </Protected>
      }
    />
    <Route
      path="/initiative-page/:pageName"
      element={
        <Protected userType="student">
          <InitiativePage />
        </Protected>
      }
    />
    <Route
      path="/dashboard/student/*"
      element={
        <Protected userType="student">
          <StudentDashboard />
        </Protected>
      }
    />
    <Route
      path="/college-costs/*"
      element={
        <Protected userType="student">
          <CollegeCosts />
        </Protected>
      }
    />
    <Route
      path="/course-plans"
      element={
        <Protected userType="student">
          <StudentPlanListing />
        </Protected>
      }
    />
    <Route
      path="/course-planner/:studentPlanUUID/:requirementUUID"
      element={
        <Protected userType="student">
          <CoursePlanner />
        </Protected>
      }
    />
    <Route
      path="/careers/*"
      element={
        <Protected userType="student">
          <CareerCenterPages />
        </Protected>
      }
    />
    <Route
      path="/badges/*"
      element={
        <Protected userType="student">
          <DigitalBadges />
        </Protected>
      }
    />
    <Route
      path="/plans/*"
      element={
        <Protected userType="student">
          <PersonalizedPlans />
        </Protected>
      }
    />
    <Route
      path="/surveys/wyr/*"
      element={
        <Protected userType="student">
          <Surveys />
        </Protected>
      }
    />
    <Route
      path="/assessments/top-skills/*"
      element={
        <Protected userType="student">
          <SkillsAssessment />
        </Protected>
      }
    />
    <Route
      path={`${EL_VIRTUAL_TOURS}/*`}
      element={
        <Protected userType="student">
          <VirtualTours isElementary />
        </Protected>
      }
    />
    <Route
      path={`${VIRTUAL_TOURS}/*`}
      element={
        <Protected userType="student">
          <VirtualTours />
        </Protected>
      }
    />
    <Route
      path="/el/learning/*"
      element={
        <Protected userType="student">
          <LearningTasks isElementary />
        </Protected>
      }
    />
    <Route
      path="/learning/*"
      element={
        <Protected userType="student">
          <LearningTasks />
        </Protected>
      }
    />
    <Route
      path="/goals/*"
      element={
        <Protected userType="student">
          <StudentGoals />
        </Protected>
      }
    />
    <Route
      path="/opportunities/*"
      element={
        <Protected userType="student">
          <Opportunities />
        </Protected>
      }
    />
    <Route
      path="/employer-showcase"
      element={
        <Protected userType="student">
          <EmployerShowcase />
        </Protected>
      }
    />
    <Route path="/employer-showcase">
      <Route
        path=":employerId"
        element={
          <Protected userType="student">
            <ShowcaseProfile />
          </Protected>
        }
      />
      <Route
        index
        element={
          <Protected userType="student">
            <EmployerShowcase />
          </Protected>
        }
      />
    </Route>
    <Route
      path="/student-pathways/*"
      element={
        <Protected userType="student">
          <StudentPathways />
        </Protected>
      }
    />
    <Route
      path="/credential-wallet/*"
      element={
        <Protected userType="student">
          <CredentialWallet />
        </Protected>
      }
    />
    <Route
      path="/document-center"
      element={
        <Protected userType="student">
          <DocumentCenter allowImageBasedPdf />
        </Protected>
      }
    />
    <Route
      path="/record-requests/*"
      element={
        <Protected userType="student">
          <RecordCenter isStudent />
        </Protected>
      }
    />
    <Route
      path="/notifications"
      element={
        <Protected userType="student">
          <Notifications />
        </Protected>
      }
    />
    <Route
      path="/account-settings/*"
      element={
        <Protected userType="student">
          <AccountSettings />
        </Protected>
      }
    />
    <Route
      path="/test-scores"
      element={
        <Protected userType="student">
          <TestScores />
        </Protected>
      }
    />
    <Route
      path="/experience-tracking"
      element={
        <Protected userType="student">
          <StudentExperienceTrackingLandingPage />
        </Protected>
      }
    />
    <Route
      path="/experience-tracking/:id"
      element={
        <Protected userType="student">
          <StudentExperienceTrackingDetailPage />
        </Protected>
      }
    />
    <Route
      path="/visit-notification/:notificationId"
      element={
        <Protected userType="student">
          <VisitNotification />
        </Protected>
      }
    />
    <Route
      path="/messages/*"
      element={
        <Protected userType="student">
          <MessageCenter />
        </Protected>
      }
    />
    <Route
      path="/profile/edit"
      element={
        <Protected userType="student">
          <StudentProfile mode="edit" type="student" />
        </Protected>
      }
    />
    <Route
      path="/profile/preview"
      element={
        <Protected userType="student">
          <StudentProfile mode="preview" type="student" />
        </Protected>
      }
    />
    <Route
      path="/students/:studentId/profile"
      element={<StudentProfile mode="view" type="student" fetchUserProfile />}
    />
    <Route
      path="/members/:memberId/profile"
      element={<StudentProfile mode="view" type="member" />}
    />
    <Route
      path="/onboarding/student"
      element={
        <Protected userType="student">
          <PathV2WelcomeView />
        </Protected>
      }
    />
    <Route
      path="/onboarding/student/*"
      element={
        <Protected userType="student">
          <OnboardingAssessment />
        </Protected>
      }
    />
    <Route
      path="/assessments/*"
      element={
        <Protected userType="student">
          <Assessments />
        </Protected>
      }
    />
    <Route
      path="/schools/final-list/*"
      element={
        <Protected userType="student">
          <FinalList />
        </Protected>
      }
    />
    <Route
      path="/schools/admission-stats"
      element={
        <Protected userType="student">
          <FinalList />
        </Protected>
      }
    />
    <Route
      path="/schools/*"
      element={
        <Protected userType="student">
          <SchoolSearch />
        </Protected>
      }
    />
    <Route
      path="/school-profile/:schoolId"
      element={
        <Protected>
          <SchoolProfile />
        </Protected>
      }
    />
    <Route
      path="/school-profile/:schoolId/:schoolName"
      element={<SchoolProfile />}
    />
    <Route
      path="/enrollment-decision"
      element={
        <Protected userType="student">
          <EnrollmentDecision />
        </Protected>
      }
    />
    <Route
      path="/college-applications/*"
      element={
        <Protected userType="student">
          <CollegeApplicationManager />
        </Protected>
      }
    />
    <Route
      path="/game-of-life/*"
      element={
        <Protected userType="student">
          <LifeSimulator />
        </Protected>
      }
    />
    <Route
      path="/scholarship-matching"
      element={
        <Protected userType="student">
          <ScholarshipMatching />
        </Protected>
      }
    />
    <Route
      path="/key-readiness-indicators/*"
      element={
        <Protected userType="student">
          <KeyReadinessIndicators />
        </Protected>
      }
    />
    <Route
      path="/senior-survey"
      element={
        <Protected userType="student">
          <SeniorSurvey />
        </Protected>
      }
    />
    <Route
      path="/program-exploration/*"
      element={
        <Protected userType="student">
          <IPPEStudentRoutes />
        </Protected>
      }
    />
    <Route
      path="/events/:eventId/k12-user/redirect/"
      element={
        <Protected userType="student">
          <K12UsersEventRedirect />
        </Protected>
      }
    />
    <Route
      path="/event-scheduler/survey/:surveyInstanceId/:userSurveyId/:eventId"
      element={
        <Protected userType="student">
          <EventSchedulerSurveys />
        </Protected>
      }
    />
    <Route
      path="/brag-sheet-survey/*"
      element={
        <Protected userType="student">
          <BragSheetSurvey />
        </Protected>
      }
    />
    <Route path="/college-athletic-eligibility" element={<NCAAEligibility />} />
    <Route
      path="/sitemap"
      element={
        <Protected userType="student">
          <Sitemap />
        </Protected>
      }
    />
  </>
);

export default StudentRoutes;
